import { Deal } from '../types';
import { supabase } from './supabase';
import { sanitizerService } from './sanitizerService';
import { ExtendedError } from '../types';
import { apiService } from './apiService';
import { config } from './config';
import errorHandler, { AppError } from '../utils/errorHandler';
import logger from '../utils/loggerService';

// API URL from centralized config
const API_URL = config.apiUrl;

// Remove client-side admin caching for security reasons
// DO NOT cache admin status on the client as it creates a security vulnerability

// Add type definition for DealSubmission
interface DealSubmission {
  title: string;
  description: string;
  price: number;
  merchant: string;
  category: string;
  location: string;
  image_url: string;
  deal_url: string; 
}

// Add interface for voucher code submission
interface VoucherCodeSubmission {
  code: string;
  description?: string;
  merchant?: string;
  merchant_url?: string;
  discount_value?: string | number;
  discount_type?: 'percentage' | 'money';
  expiry_date?: string | Date;
  brand_image_url?: string;
}

// Add interface for voucher code data
interface VoucherCode {
  id: string;
  code: string;
  description?: string;
  merchant?: string;
  merchant_url?: string;
  discount_value?: string | number;
  discount_type?: 'percentage' | 'money';
  expiry_date?: string;
  created_at: string;
  user_id: string;
  profiles?: { username: string };
  submitted_by?: string;
}

// Use our consistent error handler instead of the old one
const handleApiError = (error: any, defaultMessage: string): AppError => {
  return errorHandler.handleApiError(error, defaultMessage);
};

export const api = {
  // Get filtered deals with pagination based on sort option
  async getFilteredDeals({ 
    category, 
    sort = 'latest', 
    location, 
    date,
    page = 1, 
    limit = 10 
  }: { 
    category?: string; 
    sort?: string;
    location?: string; 
    date?: string;
    page?: number; 
    limit?: number 
  }): Promise<{ deals: Deal[]; totalPages: number; totalItems: number }> {
    try {
      // If showing hottest deals, use the special RPC function
      if (sort === 'hottest') {
        return this.getMostPopularDeals({ category, location, date, page, limit });
      }
      
      // For other sort options, use regular queries
      let query = supabase
        .from('deals_with_temperature')
        .select('*', { count: 'exact' })
        .eq('status', 'active');
      
      // Apply category filter if provided and not 'all'
      if (category && category !== 'all') {
        query = query.eq('category', category);
      }
      
      // Apply location filter if provided
      if (location) {
        query = query.ilike('location', `%${location}%`);
      }
      
      // Apply date filter if provided
      if (date) {
        let startDate;
        const endDate = new Date();
        
        if (date === 'today') {
          startDate = new Date();
          startDate.setHours(0, 0, 0, 0);
        } else if (date === 'week') {
          startDate = new Date();
          startDate.setDate(startDate.getDate() - 7);
        } else if (date === 'month') {
          startDate = new Date();
          startDate.setMonth(startDate.getMonth() - 1);
        } else if (date === 'year') {
          startDate = new Date();
          startDate.setFullYear(startDate.getFullYear() - 1);
        }
        
        if (startDate) {
          query = query.gte('created_at', startDate.toISOString());
        }
      }
      
      // Apply sort options
      if (sort === 'latest') {
        query = query.order('created_at', { ascending: false });
      } else if (sort === 'price_low') {
        query = query.order('price', { ascending: true });
      } else if (sort === 'price_high') {
        query = query.order('price', { ascending: false });
      }
      
      // Apply pagination
      const start = (page - 1) * limit;
      const end = start + limit - 1;
      query = query.range(start, end);
      
      // Execute the query
      const { data, error, count } = await query;
      
      if (error) throw error;
      
      const totalItems = count || 0;
      const totalPages = Math.ceil(totalItems / limit);
      
      return {
        deals: data || [],
        totalPages: totalPages,
        totalItems: totalItems
      };
    } catch (error) {
      throw handleApiError(error, 'Error getting filtered deals');
    }
  },
  
  // Get deal by ID with comments
  async getDeal(id: string | number) {
    console.log("API.getDeal called with ID:", id);
    
    try {
      // Check if id is undefined or empty
      if (id === undefined || id === null || id === '' || id === 'undefined') {
        console.error("Invalid ID provided to getDeal:", id);
        throw new Error('Deal ID is required');
      }
      
      // Check if the provided ID is a shortened version (prefix)
      const idString = String(id).trim();
      
      if (idString === '') {
        console.error("ID string is empty after trimming");
        throw new Error('Empty deal ID provided');
      }
      
      const isLikelyPrefix = idString.length < 32 && !idString.includes('-');
      
      console.log("Processing deal ID:", idString, "isLikelyPrefix:", isLikelyPrefix);
      
      // First try with the get_deal RPC function
      let data;
      let error;
      
      try {
        console.log("Calling RPC get_deal with param:", idString);
        const result = await supabase
          .rpc('get_deal', {
            deal_id_param: idString
          });
        
        data = result.data;
        error = result.error;
        
        if (error) {
          console.log("RPC get_deal error:", error.message);
        } else if (!data) {
          console.log("RPC get_deal returned no data");
        } else {
          console.log("RPC get_deal successful, data type:", Array.isArray(data) ? "Array" : typeof data);
          if (Array.isArray(data)) {
            console.log("Array length:", data.length);
          }
        }
      } catch (e) {
        error = e;
        console.error("Exception in RPC get_deal call:", e);
      }
      
      // If RPC fails and the ID looks like a prefix, try a fallback search by prefix
      if ((error || !data) && isLikelyPrefix) {
        console.log("RPC call failed with shortened ID, trying fallback search", idString);
        
        // Search for a deal with an ID that starts with our prefix
        const { data: dealsWithPrefix, error: searchError } = await supabase
          .from('deals')
          .select(`
            *,
            profiles:user_id (
              username
            )
          `)
          .like('id', `${idString}%`)
          .limit(1);
        
        if (searchError) {
          console.error("Prefix search error:", searchError);
          throw searchError;
        }
        
        if (dealsWithPrefix && dealsWithPrefix.length > 0) {
          console.log("Found deal via prefix search:", dealsWithPrefix[0].id);
          
          // Store the mapping for future use
          if (typeof window !== 'undefined' && window.localStorage) {
            try {
              const shortId = idString;
              const fullId = dealsWithPrefix[0].id;
              
              if (!fullId) {
                console.error("Found deal has null/undefined ID");
                throw new Error('Invalid deal data returned from database');
              }
              
              // Update localStorage with the mapping
              const existingMappings = localStorage.getItem('dealIdMappings') || '{}';
              const mappings = JSON.parse(existingMappings);
              mappings[shortId] = fullId;
              localStorage.setItem('dealIdMappings', JSON.stringify(mappings));
              
              console.log(`Stored mapping: ${shortId} -> ${fullId}`);
            } catch (e) {
              console.error("Error storing ID mapping:", e);
            }
          }
          
          // Use the found deal
          data = dealsWithPrefix[0];
          
          // Calculate temperature (since we're bypassing the get_deal RPC which would do this)
          const hotVotes = data.hot_votes || 0;
          const coldVotes = data.cold_votes || 0;
          const tempOffset = data.temperature_offset || 0;
          
          // Simple calculation similar to what's in the database
          data.calculated_temperature = hotVotes - coldVotes + tempOffset;
        } else {
          console.error("No deals found with prefix:", idString);
        }
      }
      
      // If we still don't have data, throw an error
      if (error) throw error;
      if (!data) throw new Error('Deal not found');
      
      // Handle the case where data is an array (which seems to be happening)
      let dealData = data;
      if (Array.isArray(data)) {
        console.log("Data returned as array, extracting first item:", data);
        if (data.length === 0) {
          throw new Error('Deal not found');
        }
        dealData = data[0]; // Take the first item from the array
      }
      
      // Double check that the deal has an ID
      if (!dealData.id) {
        console.error("Retrieved deal is missing an ID field:", dealData);
        throw new Error('Retrieved deal is missing ID field');
      }
      
      // Ensure all required fields have at least default values
      const validatedDeal = {
        ...dealData,
        id: dealData.id, // Ensure ID is present
        title: dealData.title || 'Unnamed Deal',
        description: dealData.description || '',
        price: dealData.price || 0,
        merchant: dealData.merchant || 'Unknown Merchant',
        deal_url: dealData.deal_url || '',
        image_url: dealData.image_url || 'https://images.islamicdeals.com/uploads/default.jpg',
        created_at: dealData.created_at || new Date().toISOString()
      };
      
      // Get comments for this deal (without trying to join with profiles)
      // Make sure we have a valid ID for the comments query
      if (!validatedDeal.id) {
        console.error("validatedDeal is missing ID after validation:", validatedDeal);
        
        // Try using the original dealData.id as fallback
        if (!dealData || !dealData.id) {
          throw new Error('Deal ID is missing');
        }
        
        console.log("Using original dealData.id as fallback for comments query:", dealData.id);
        validatedDeal.id = dealData.id;
      }
      
      const { data: comments, error: commentsError } = await supabase
        .from('comments')
        .select('*')
        .eq('deal_id', validatedDeal.id) // Use the full ID for comments query
        .order('created_at', { ascending: false });
      
      if (commentsError) {
        console.error("Error fetching comments:", commentsError);
        throw commentsError;
      }
      
      // If we have comments, fetch the associated usernames in a separate query
      let commentsWithProfiles = comments || [];
      if (commentsWithProfiles.length > 0) {
        // Extract unique user IDs from comments
        const userIdSet = new Set(commentsWithProfiles.map((comment: any) => comment.user_id));
        const userIds = Array.from(userIdSet);
        
        // Fetch profiles for these users
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, username')
          .in('id', userIds);
          
        if (!profilesError && profiles) {
          // Create a map of userId -> username for quick lookups
          const profileMap = profiles.reduce((map: Record<string, { username: string }>, profile: any) => {
            map[profile.id] = { username: profile.username };
            return map;
          }, {});
          
          // Add profile data to each comment
          commentsWithProfiles = commentsWithProfiles.map((comment: any) => ({
            ...comment,
            profiles: profileMap[comment.user_id] || { username: 'Unknown User' }
          }));
        }
      }
      
      return {
        deal: validatedDeal,
        comments: commentsWithProfiles
      };
    } catch (error) {
      throw handleApiError(error, 'Error getting deal');
    }
  },
  
  // Vote on a deal (hot/cold)
  async voteDeal(dealId: string | number, voteType: boolean | 'hot' | 'cold') {
    let rpcError: any = null; // Variable to store RPC error if it occurs
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) throw new Error('Not authenticated');

      const dealIdStr = String(dealId);
      const isHot = typeof voteType === 'boolean' ? voteType : voteType === 'hot';

      const { data, error } = await supabase.rpc('secure_vote_deal', {
        deal_id_param: dealIdStr,
        is_hot_param: isHot
      });
      
      // Store the RPC error if it exists
      if (error) {
          rpcError = error;
          // Log only if it's NOT the specific 'already voted' error
          const errorMsg = (error.message || '').toLowerCase();
          if (!errorMsg.includes('already voted')) {
              logger.error('Error returned from secure_vote_deal RPC', error);
          }
          throw new Error(error.message || 'RPC Error', { cause: error });
      }
      
      if (!data || data.success !== true) {
         logger.warn('secure_vote_deal RPC did not return success:true', data);
         throw new Error(data?.message || 'Voting failed: Backend did not confirm success.');
      }
      
      return {
        success: true,
        hotVotes: data.hotVotes,
        coldVotes: data.coldVotes
      };
    } catch (error: any) {
       // Check if the error we caught is the SAME one from the RPC call above
       // OR if it's a different error caught within the try block
       const isHandledVoteError = 
           (rpcError && error.cause === rpcError && (rpcError.message || '').toLowerCase().includes('already voted')) ||
           (error.message || '').toLowerCase().includes('already voted');

       // Log only if it's NOT the handled 'already voted' error
       if (!isHandledVoteError) {
           logger.error('Error caught within api.voteDeal', error);
       }
       
       // Still pass the error through the handler for consistent formatting
       // The handler itself might still log, but we've stopped logging here.
       throw handleApiError(error, 'Error voting on deal');
    }
  },
  
  // Submit a new deal
  async submitDeal(deal: DealSubmission) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) throw new Error('Not authenticated');
  
      const { data, error } = await supabase
        .rpc('secure_submit_deal', {
          title_param: deal.title,
          description_param: deal.description,
          price_param: deal.price,
          merchant_param: deal.merchant,
          category_param: deal.category,
          location_param: deal.location,
          image_url_param: deal.image_url,
          deal_url_param: deal.deal_url
        });
      
      if (error) throw error;
      return data;
    } catch (error) {
      throw handleApiError(error, 'Error submitting deal');
    }
  },

  // Add a comment to a deal
  async addComment(dealId: string, content: string, parentId?: string) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .rpc('secure_add_comment', {
          deal_id_param: dealId,
          user_id_param: userId,
          content_param: content,
          parent_id_param: parentId || null
        });
      
      if (error) throw error;
      return data;
    } catch (error) {
      throw handleApiError(error, 'Error adding comment');
    }
  },


// Get monthly leaderboard of users by total temperatures
async getMonthlyLeaderboard(currentMonthOnly = true, limit = 20) {
  try {
    const { data, error } = await supabase.rpc('get_monthly_temperature_leaderboard', {
      current_month_only: currentMonthOnly,
      limit_param: limit
    });

    if (error) throw error;
    
    return { 
      leaderboard: data || [],
      month: currentMonthOnly ? new Date().toLocaleString('default', { month: 'long', year: 'numeric' }) : 'All Time'
    };
  } catch (error) {
    throw handleApiError(error, 'Error fetching leaderboard data');
  }
},

async createBlogPost(postData: any) {
  try {
    const { data, error } = await supabase
      .from('blog_posts')
      .insert([{
        ...postData,
        user_id: (await supabase.auth.getSession()).data.session?.user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        published_at: postData.status === 'published' ? new Date().toISOString() : null
      }])
      .select();
    
    if (error) throw error;
    return { post: data[0] };
  } catch (error) {
    throw handleApiError(error, 'Error creating blog post');
  }
},

// Update an existing blog post
async updateBlogPost(id: string, postData: any) {
  try {
    const updateData = {
      ...postData,
      updated_at: new Date().toISOString(),
      published_at: postData.status === 'published' ? 
        (await this.getBlogPostById(id)).post.published_at || new Date().toISOString() : 
        null
    };
    
    const { data, error } = await supabase
      .from('blog_posts')
      .update(updateData)
      .eq('id', id)
      .select();
    
    if (error) throw error;
    return { post: data[0] };
  } catch (error) {
    throw handleApiError(error, 'Error updating blog post');
  }
},

// Get a blog post by ID
async getBlogPostById(id: string) {
  try {
    // First get the blog post
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    
    // Get author name in a separate query
    let authorName = 'Unknown Author';
    if (data.user_id) {
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', data.user_id)
        .single();
        
      if (!userError && userData) {
        authorName = userData.username;
      }
    }
    
    // Format the response
    const formattedPost = {
      ...data,
      author_name: authorName
    };
    
    return { post: formattedPost };
  } catch (error) {
    throw handleApiError(error, 'Error getting blog post');
  }
},

// Get a blog post by slug
async getBlogPostBySlug(slug: string) {
  try {
    // First get the blog post
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('slug', slug)
      .single();
    
    if (error) throw error;
    
    // Get author name in a separate query
    let authorName = 'Unknown Author';
    if (data.user_id) {
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', data.user_id)
        .single();
        
      if (!userError && userData) {
        authorName = userData.username;
      }
    }
    
    // Format the response
    const formattedPost = {
      ...data,
      author_name: authorName
    };
    
    return { post: formattedPost };
  } catch (error) {
    throw handleApiError(error, 'Error getting blog post');
  }
},

// Get all blog posts
async getBlogPosts() {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    
    // First check if the user is an admin
    let isAdmin = false;
    if (userId) {
      const { data: adminCheck } = await supabase
        .rpc('check_admin_status', { user_id: userId });
      isAdmin = !!adminCheck;
    }
    
    // Fixed query that uses string literals properly in the OR condition
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .or(isAdmin 
        ? 'status.eq.published,status.eq.draft' 
        : `status.eq.published${userId ? ',and(status.eq.draft,user_id.eq.' + userId + ')' : ''}`)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    if (!data || data.length === 0) {
      return { posts: [] };
    }
    
    // Fix: Convert Set to Array without using spread operator
    const userIdSet = new Set<string>();
    data.forEach(post => {
      if (post.user_id) userIdSet.add(post.user_id);
    });
    const userIds = Array.from(userIdSet);
    
    const { data: profilesData, error: profilesError } = await supabase
      .from('profiles')
      .select('id, username')
      .in('id', userIds);
    
    if (profilesError) throw profilesError;
    
    // Create a map of user IDs to usernames
    const usernameMap: Record<string, string> = {};
    if (profilesData) {
      profilesData.forEach(profile => {
        usernameMap[profile.id] = profile.username;
      });
    }
    
    // Add usernames to posts
    const postsWithUsernames = data.map(post => ({
      ...post,
      author_name: usernameMap[post.user_id] || 'Unknown'
    }));
    
    return { posts: postsWithUsernames };
  } catch (error) {
    throw handleApiError(error, 'Error getting blog posts');
  }
},

// Delete a blog post
async deleteBlogPost(id: string) {
  try {
    const { error } = await supabase
      .from('blog_posts')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    return { success: true };
  } catch (error) {
    throw handleApiError(error, 'Error deleting blog post');
  }
},

  // Get user profile with optimized queries
  async getUserProfile(userId: string) {
    try {
      logger.info('Getting user profile', { userId });
      
      if (!userId) {
        logger.error('getUserProfile called with empty userId');
        throw new Error('User ID is required');
      }
      
      // First get the profile data
      let { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      logger.debug('Profile query result', { profile, profileError });
      
      if (profileError) {
        // If the profile doesn't exist, check if the user is authenticated
        if (profileError.code === 'PGRST116') {
          // Get the current authenticated user (if any)
          const { data: authData } = await supabase.auth.getUser();
          const authUser = authData?.user;
          
          // If we're looking for the current user's profile, create it
          if (authUser && authUser.id === userId) {
            logger.info('Profile not found for authenticated user, creating default profile');
            
            try {
              // Create a default profile
              const defaultProfile = {
                id: userId,
                username: authUser.user_metadata?.username || authUser.email?.split('@')[0] || 'user',
                created_at: new Date().toISOString(),
                profile_image_url: null,
                pending_profile_image_url: null,
                profile_image_status: 'none' as const
              };
              
              logger.debug('Creating default profile', defaultProfile);
              
              // Insert the default profile
              const { data: newProfile, error: insertError } = await supabase
                .from('profiles')
                .insert(defaultProfile)
                .select()
                .single();
              
              logger.debug('Default profile creation result', { newProfile, insertError });
              
              if (insertError) {
                logger.error('Error creating default profile', insertError);
                throw insertError;
              }
              
              if (!newProfile) {
                logger.error('No profile returned after creation');
                throw new Error('Failed to create profile');
              }
              
              // Use the new profile
              profile = newProfile;
            } catch (createError) {
              logger.error('Error in profile creation', createError);
              throw createError;
            }
          } else {
            // We're looking for another user's profile that doesn't exist
            logger.error('Requested profile does not exist', { userId });
            throw new Error('User profile not found');
          }
        } else {
          logger.error('Profile query error', profileError);
          throw profileError;
        }
      }
      
      if (!profile) {
        logger.error('No profile data available after queries');
        throw new Error('Profile data not available');
      }
      
      logger.debug('Profile data before counts', profile);
      
      // Then get the counts in separate queries
      const countsPromises = [
        supabase.from('deals').select('*', { count: 'exact', head: true }).eq('user_id', userId),
        supabase.from('comments').select('*', { count: 'exact', head: true }).eq('user_id', userId),
        supabase.from('deal_votes').select('*', { count: 'exact', head: true }).eq('user_id', userId)
      ];
      
      const [dealsResult, commentsResult, votesResult] = await Promise.all(countsPromises);
      
      logger.debug('Count results', { 
        deals: dealsResult, 
        comments: commentsResult, 
        votes: votesResult 
      });
      
      const result = {
        user: {
          ...profile,
          deals_count: dealsResult.count || 0,
          comments_count: commentsResult.count || 0,
          votes_count: votesResult.count || 0
        }
      };
      
      logger.debug('getUserProfile returning', result);
      return result;
    } catch (error) {
      throw handleApiError(error, 'Error getting user profile');
    }
  },
  
  // Get user deals with pagination
  async getUserDeals(userId: string, page = 1, limit = 12) {
    try {
      // Calculate pagination range
      const start = (page - 1) * limit;
      const end = start + limit - 1;
      
      // Get paginated deals
      const { data: deals, error, count } = await supabase
        .from('deals')
        .select('*', { count: 'exact' })
        .eq('user_id', userId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .range(start, end);
      
      if (error) throw error;
      
      // Calculate total pages
      const totalPages = count ? Math.ceil(count / limit) : 1;
      
      return { 
        deals, 
        totalItems: count || 0, 
        totalPages 
      };
    } catch (error) {
      throw handleApiError(error, 'Error getting user deals');
    }
  },
  
  // Get user's comments
  async getUserComments(userId: string) {
    try {
      // First, fetch comments without joining
      const { data: commentsData, error: commentsError } = await supabase
        .from('comments')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      if (commentsError) throw commentsError;
      
      if (!commentsData || commentsData.length === 0) {
        return { comments: [] };
      }
      
      // Extract deal IDs from comments
      const dealIds = Array.from(new Set(commentsData.map(comment => comment.deal_id)));
      
      // Fetch deal titles separately
      const { data: dealsData, error: dealsError } = await supabase
        .from('deals')
        .select('id, title')
        .in('id', dealIds);
      
      if (dealsError) {
        logger.error('Error fetching deal titles', dealsError);
        // Return comments even if deals fetch fails
        return { comments: commentsData };
      }
      
      // Create a map of deal IDs to titles
      const dealTitleMap: Record<number, string> = {};
      dealsData?.forEach(deal => {
        dealTitleMap[deal.id] = deal.title;
      });
      
      // Combine comments with deal titles
      const commentsWithDeals = commentsData.map(comment => ({
        ...comment,
        deals: { title: dealTitleMap[comment.deal_id] || `Deal #${comment.deal_id}` }
      }));
      
      return { comments: commentsWithDeals };
    } catch (error) {
      throw handleApiError(error, 'Error getting user comments');
    }
  },

   // Update profile image (set as pending)
   async updateProfileImage(userId: string, imageUrl: string) {
    try {
      // Get current user data
      const { data: authData } = await supabase.auth.getUser();
      const currentUser = authData?.user;
      
      if (!currentUser) {
        throw new Error('User authentication data not available');
      }
      
      // Update profile directly
      const { data, error } = await supabase
        .from('profiles')
        .update({
          pending_profile_image_url: imageUrl,
          profile_image_status: 'pending'
        })
        .eq('id', userId)
        .select();
      
      if (error) throw error;
      return data?.[0];
    } catch (error) {
      throw handleApiError(error, 'Error updating profile image');
    }
  },

  async checkUsernameAvailability(username: string): Promise<boolean> {
    try {
      // Input validation
      if (!username || username.length < 3 || !/^[a-zA-Z0-9_]+$/.test(username)) {
        return false;
      }
      
      // Apply rate limiting (implement in a separate service)
      // this.rateLimitService.checkLimit(username, 'username_check');
      
      const { data, error } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', username.toLowerCase()) // Case-insensitive comparison
        .maybeSingle();
      
      if (error) {
        console.error('Error checking username:', error);
        // On error, default to unavailable for security
        return false;
      }
      
      // If data exists, username is taken
      return data === null;
    } catch (error) {
      console.error('Error checking username availability:', error);
      // Default to unavailable in case of error
      return false;
    }
  },

  // Check if a username already exists
  async checkUsernameExists(username: string): Promise<boolean> {
    try {
      const { count, error } = await supabase
        .from('profiles')
        .select('username', { count: 'exact', head: true })
        .eq('username', username);
        
      if (error) throw error;
      
      // If count is greater than 0, username exists
      return count !== null && count > 0;
    } catch (error) {
      logger.error('Error checking username existence', error);
      // Return false on error to allow the registration to proceed
      // The server-side validation will catch duplicate usernames
      return false;
    }
  },

  // Admin: Get pending profile images with optimized query
  async getPendingProfileImages() {
    return verifyAdminOperation(async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, pending_profile_image_url, created_at, profile_image_status')
        .eq('profile_image_status', 'pending')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return {
        pendingImages: data.map(item => ({
          user_id: item.id,
          username: item.username,
          pending_profile_image_url: item.pending_profile_image_url,
          submission_date: item.created_at,
          status: item.profile_image_status as 'pending' | 'approved' | 'rejected'
        })) || []
      };
    }, 'Error getting pending profile images');
  },

  // Admin: Moderate profile image with transaction
  async moderateProfileImage(userId: string, action: 'approve' | 'reject') {
    return verifyAdminOperation(async () => {
      // Start a transaction
      const { data: profile, error: fetchError } = await supabase
        .from('profiles')
        .select('pending_profile_image_url')
        .eq('id', userId)
        .single();
        
      if (fetchError) throw fetchError;
      if (!profile) throw new Error('Profile not found');
      if (!profile.pending_profile_image_url) throw new Error('No pending image found');
      
      // Store the image URL for potential deletion
      const pendingImageUrl = profile.pending_profile_image_url;
      
      let updateData = {};
      if (action === 'approve') {
        updateData = {
          profile_image_url: pendingImageUrl,
          pending_profile_image_url: null,
          profile_image_status: 'approved'
        };
      } else {
        updateData = {
          pending_profile_image_url: null,
          profile_image_status: 'rejected'
        };
      }
      
      // Update without optimistic locking
      const { data, error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', userId)
        .select();
      
      if (error) throw error;
      
      // If rejecting, delete the image from R2
      if (action === 'reject' && pendingImageUrl) {
        try {
          // Import here to avoid circular dependencies
          const { deleteImageFromR2 } = await import('../utils/imageCleanup');
          
          // Delete the image asynchronously - don't wait for it to complete
          // to avoid blocking the UI if deletion takes time
          deleteImageFromR2(pendingImageUrl)
            .then(success => {
              if (success) {
                logger.info(`Rejected profile image deleted from R2: ${pendingImageUrl}`);
              } else {
                logger.error(`Failed to delete rejected profile image: ${pendingImageUrl}`);
              }
            })
            .catch(deleteError => {
              logger.error('Error during rejected image cleanup', deleteError);
            });
        } catch (importError) {
          logger.error('Failed to import deleteImageFromR2', importError);
        }
      }
      
      return { user: data?.[0] };
    }, `Error ${action}ProfileImage`);
  },

  // Update the isAdmin function to always verify admin status with the server
// Update the isAdmin function to call check_admin_status without parameters
async isAdmin() {
  try {
    // Get current session
    const { data: { session } } = await supabase.auth.getSession();
    
    // If no user is logged in, they can't be an admin
    if (!session?.user?.id) return false;
    
    // SECURITY: Always verify admin status directly with the server
    // Call the parameterless version of check_admin_status
    const { data, error } = await supabase
      .rpc('check_admin_status');
    
    if (error) {
      // Fail closed: any error means no admin access
      console.error('Admin check error:', error);
      return false;
    }
    
    // Return the server-verified admin status
    return !!data;
  } catch (error) {
    // Fail closed: any exception means no admin access
    console.error('Admin check exception:', error);
    return false;
  }
},

// Get pending deals that need approval
async getPendingDeals() {
  try {
    // First verify admin status
    const isAdmin = await this.isAdmin();
    if (!isAdmin) {
      throw new Error('Unauthorized: Admin access required');
    }
    
    const { data, error } = await supabase.rpc('get_pending_deals');
    
    if (error) throw error;
    console.log("Pending deals fetched:", data); // Debug output
    return { pendingDeals: data || [] };
  } catch (error) {
    console.error("Error fetching pending deals:", error);
    throw handleApiError(error, 'Failed to fetch pending deals');
  }
},

// Moderate a deal (approve/reject)
async moderateDeal(dealId: string, action: 'approve' | 'reject') {
  try {
    const { data, error } = await supabase.rpc('moderate_deal', { 
      deal_id_param: dealId,
      action_param: action
    });
    
    if (error) throw error;
    return { success: true };
  } catch (error) {
    throw handleApiError(error, 'Failed to moderate deal');
  }
},  

  // Admin: Update deal as admin using secure RPC function
  async updateDealAsAdmin(dealId: string, dealData: any) {
    try {
      const { data, error } = await supabase.rpc('admin_update_deal', {
        deal_id_param: dealId, 
        deal_data: dealData
      });
      
      if (error) throw error;
      return data;
    } catch (error) {
      throw handleApiError(error, 'Failed to update deal as admin');
    }
  },

  // Search deals
  async searchDeals(term: string) {
    try {
      if (!term.trim()) {
        return { deals: [] };
      }
      
      // Sanitize the search term to prevent injection attacks
      const sanitizedTerm = sanitizerService.sanitizeText(term.trim());
      
      // Only include title and merchant in the search query
      const searchQuery = `title.ilike.%${sanitizedTerm}%,merchant.ilike.%${sanitizedTerm}%`;
      
      const { data, error } = await supabase
        .from('deals')
        .select('*')
        .eq('status', 'active')
        .or(searchQuery)
        .order('created_at', { ascending: false })
        .limit(20);
      
      if (error) throw error;
      
      return { deals: data || [] };
    } catch (error) {
      throw handleApiError(error, 'Error searching deals');
    }
  },

  // Search for deals by ID prefix (used for direct URL access)
  async searchDealsByIdPrefix(idPrefix: string) {
    console.log(`[API] searchDealsByIdPrefix called with prefix: ${idPrefix}`);
    try {
      // Validate the prefix is exactly 7 characters, as expected by the slug generator
      if (!idPrefix || idPrefix.length !== 7) { 
        console.log('[API] searchDealsByIdPrefix: Invalid prefix (must be 7 chars), returning empty.');
        return { data: [] };
      }

      console.log('[API] searchDealsByIdPrefix: Calling RPC search_deals_by_id_prefix with prefix:', idPrefix);

      // Call the new database function
      const { data, error } = await supabase
        .rpc('search_deals_by_id_prefix', {
          prefix: idPrefix
        });

      if (error) {
        console.error('[API] searchDealsByIdPrefix: Supabase RPC error:', error);
        // Don't throw, return empty data so the UI can handle 'not found'
        return { data: [] }; 
      }

      console.log('[API] searchDealsByIdPrefix: RPC successful. Result:', data);
      // Ensure the result is always an array, even if RPC returns a single object or null
      return { data: Array.isArray(data) ? data : (data ? [data] : []) };
    } catch (error) {
      console.error('[API] searchDealsByIdPrefix: Caught error:', error);
      return { data: [] }; // Return empty array on any other error
    }
  },

  // Refresh vote counts for a deal
  async refreshVoteCounts(dealId: string | number) {
    try {
      // Get vote counts directly from the deals table
      const { data, error } = await supabase
        .from('deals')
        .select('id, hot_votes, cold_votes')
        .eq('id', dealId)
        .single();
      
      if (error) {
        // If the deal doesn't exist, we'll just return default values
        if (error.code === 'PGRST116') {
          return { id: dealId, hot_votes: 0, cold_votes: 0 };
        }
        throw error;
      }
      
      // Use the values directly from the deals table
      return data;
    } catch (error) {
      throw handleApiError(error, 'Error refreshing vote counts');
    }
  },

// Get most popular deals using the server-side temperature calculation
async getMostPopularDeals({ 
  category, 
  location, 
  date,
  page = 1, 
  limit = 10 
}: { 
  category?: string; 
  location?: string; 
  date?: string;
  page?: number; 
  limit?: number 
}) {
  try {
    // Use the RPC call now that the type mismatch has been fixed in the database
    const { data, error } = await supabase
      .rpc('get_hottest_deals', {
        limit_param: limit || 10,
        offset_param: ((page || 1) - 1) * (limit || 10),
        category_filter: category === 'all' ? null : category || null,
        location_filter: location || null,
        duration_filter: date || null
      });
    
    // Handle errors
    if (error) {
      throw error;
    }

    // For pagination, we'll estimate total items based on the current page results
    // This avoids the need for a separate count query
    const hasMorePages = data && data.length === limit;
    const totalItems = hasMorePages ? (page * limit) + 1 : page * limit;
    const totalPages = hasMorePages ? page + 1 : page;

    // Format the results based on what the function actually returns
    return {
      deals: data || [],
      totalPages: totalPages,
      totalItems: totalItems
    };
  } catch (error) {
    throw handleApiError(error, 'Error getting popular deals');
  }
},

  // Batch refresh vote counts for multiple deals
async refreshVoteCountsBatch(dealIds: (string | number)[]) {
  try {
    if (dealIds.length === 0) {
      return [];
    }
    
    // Get all votes for these deals in a single query
    const { data: voteCounts, error: voteCountError } = await supabase
      .from('deal_votes')  // FIXED: Use correct table name
      .select('deal_id, vote_type')
      .in('deal_id', dealIds);
    
    if (voteCountError) {
      throw voteCountError;
    }
    
    // Process vote counts for each deal
    const dealVoteCounts: Record<string, { hot_votes: number, cold_votes: number }> = {};
    
    // Initialize counts for all requested deals
    dealIds.forEach(id => {
      const idStr = id.toString();
      dealVoteCounts[idStr] = { hot_votes: 0, cold_votes: 0 };
    });
    
    // Count votes
    voteCounts?.forEach(vote => {
      // Rest of function remains the same
        const dealId = vote.deal_id.toString();
        if (!dealVoteCounts[dealId]) {
          dealVoteCounts[dealId] = { hot_votes: 0, cold_votes: 0 };
        }
        
        if (vote.vote_type === 'hot') {
          dealVoteCounts[dealId].hot_votes++;
        } else {
          dealVoteCounts[dealId].cold_votes++;
        }
      });
      
      // Perform the batch update
      const updatesArray = Object.entries(dealVoteCounts).map(([dealId, counts]) => ({
        id: dealId,
        hot_votes: counts.hot_votes,
        cold_votes: counts.cold_votes
      }));
      
      const { error: updateError } = await supabase
        .from('deals')
        .upsert(updatesArray, { onConflict: 'id' });
      
      if (updateError) {
        throw updateError;
      }
      
      // Return the vote counts
      return updatesArray;
    } catch (error) {
      throw handleApiError(error, 'Error refreshing vote counts batch');
    }
  },

  // Get deals by category for category landing pages
  async getDealsByCategory(category: string, page = 1, limit = 10) {
    try {
      // Calculate pagination range
      const start = (page - 1) * limit;
      const end = start + limit - 1;
      
      // Get paginated deals by category
      const { data: deals, error, count } = await supabase
        .from('deals')
        .select('*', { count: 'exact' })
        .eq('category', category)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .range(start, end);
      
      if (error) throw error;
      
      // Calculate total pages
      const totalPages = count ? Math.ceil(count / limit) : 1;
      
      return { 
        deals, 
        totalItems: count || 0, 
        totalPages 
      };
    } catch (error) {
      throw handleApiError(error, `Error getting deals by category ${category}`);
    }
  },

  // Get deals by category with pagination
  async getCategoryDeals(category: string, page = 1, limit = 12) {
    try {
      // Calculate pagination range
      const start = (page - 1) * limit;
      const end = start + limit - 1;
      
      // Get paginated deals by category
      const { data: deals, error, count } = await supabase
        .from('deals')
        .select('*', { count: 'exact' })
        .eq('category', category)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .range(start, end);
      
      if (error) throw error;
      
      // Calculate total pages
      const totalPages = count ? Math.ceil(count / limit) : 1;
      
      return { 
        deals, 
        totalItems: count || 0, 
        totalPages 
      };
    } catch (error) {
      throw handleApiError(error, `Error getting deals by category ${category}`);
    }
  },

  // Replace direct report submission with secure RPC
  async reportContent(contentId: string, contentType: string, reason: string, details?: string) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) throw new Error('Not authenticated');

      const { error } = await supabase
        .rpc('secure_report_content', {
          content_id_param: contentId,
          content_type_param: contentType,
          reason_param: reason,
          details_param: details || null,
          user_id_param: userId
        });
      
      if (error) throw error;
    } catch (error) {
      throw handleApiError(error, 'Error submitting report');
    }
  },

  async setDealTemperatureOffset(dealId: string | number, offsetValue: number) {
    try {
      const { data, error } = await supabase.rpc('admin_set_deal_temperature', { 
        deal_id_param: String(dealId),
        offset_param: offsetValue
      });
      
      if (error) throw error;
      return { success: true };
    } catch (error) {
      throw handleApiError(error, 'Failed to set temperature offset');
    }
  },

  // Get user profile by username
  async getUserProfileByUsername(username: string) {
    try {
      logger.info('Getting user profile by username', { username });
      
      if (!username) {
        logger.error('getUserProfileByUsername called with empty username');
        throw new Error('Username is required');
      }
      
      // Get the profile data by username
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('username', username)
        .single();
      
      if (profileError) {
        logger.error('Profile query error by username', profileError);
        throw profileError;
      }
      
      if (!profile) {
        logger.error('No profile data available for username', { username });
        throw new Error('Profile data not available');
      }
      
      logger.debug('Profile data before counts', profile);
      
      // Get counts in separate queries using the user ID from the profile
      const userId = profile.id;
      const countsPromises = [
        supabase.from('deals').select('*', { count: 'exact', head: true }).eq('user_id', userId),
        supabase.from('comments').select('*', { count: 'exact', head: true }).eq('user_id', userId),
        supabase.from('deal_votes').select('*', { count: 'exact', head: true }).eq('user_id', userId)
      ];
      
      const [dealsResult, commentsResult, votesResult] = await Promise.all(countsPromises);
      
      const result = {
        user: {
          ...profile,
          deals_count: dealsResult.count || 0,
          comments_count: commentsResult.count || 0,
          votes_count: votesResult.count || 0
        }
      };
      
      logger.debug('getUserProfileByUsername returning', result);
      return result;
    } catch (error) {
      throw handleApiError(error, 'Error getting user profile by username');
    }
  },
  
  // Get user deals by username
  async getUserDealsByUsername(username: string, page = 1, limit = 12) {
    try {
      // First, get the user ID from the username
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', username)
        .single();
      
      if (profileError) throw profileError;
      if (!profile) throw new Error('User not found');
      
      const userId = profile.id;
      
      // Calculate pagination range
      const start = (page - 1) * limit;
      const end = start + limit - 1;
      
      // Get paginated deals
      const { data: deals, error, count } = await supabase
        .from('deals')
        .select('*', { count: 'exact' })
        .eq('user_id', userId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .range(start, end);
      
      if (error) throw error;
      
      // Calculate total pages
      const totalPages = count ? Math.ceil(count / limit) : 1;
      
      return { 
        deals, 
        totalItems: count || 0, 
        totalPages 
      };
    } catch (error) {
      throw handleApiError(error, 'Error getting user deals by username');
    }
  },
  
  // Get user's comments by username
  async getUserCommentsByUsername(username: string) {
    try {
      // First, get the user ID from the username
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', username)
        .single();
      
      if (profileError) throw profileError;
      if (!profile) throw new Error('User not found');
      
      const userId = profile.id;
      
      // Now fetch comments using the user ID
      const { data: commentsData, error: commentsError } = await supabase
        .from('comments')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      if (commentsError) throw commentsError;
      
      if (!commentsData || commentsData.length === 0) {
        return { comments: [] };
      }
      
      // Extract deal IDs from comments
      const dealIds = Array.from(new Set(commentsData.map(comment => comment.deal_id)));
      
      // Fetch deal titles separately
      const { data: dealsData, error: dealsError } = await supabase
        .from('deals')
        .select('id, title')
        .in('id', dealIds);
      
      if (dealsError) {
        logger.error('Error fetching deal titles', dealsError);
        // Return comments even if deals fetch fails
        return { comments: commentsData };
      }
      
      // Create a map of deal IDs to titles
      const dealTitleMap: Record<number, string> = {};
      dealsData?.forEach(deal => {
        dealTitleMap[deal.id] = deal.title;
      });
      
      // Combine comments with deal titles
      const commentsWithDeals = commentsData.map(comment => ({
        ...comment,
        deals: { title: dealTitleMap[comment.deal_id] || `Deal #${comment.deal_id}` }
      }));
      
      return { comments: commentsWithDeals };
    } catch (error) {
      throw handleApiError(error, 'Error getting user comments by username');
    }
  },

  // Get all voucher codes
  async getVoucherCodes(): Promise<VoucherCode[]> {
    try {
      // Fetch via RPC to include is_merchant flag and submitted_by
      const { data, error } = await supabase.rpc('get_voucher_codes');
      
      if (error) {
        console.error('Error fetching voucher codes:', error);
        throw error;
      }
      
      // The RPC returns entries with is_merchant and submitted_by already populated
      return data || [];
    } catch (error) {
      console.error('Error in getVoucherCodes:', error);
      throw handleApiError(error, 'Error fetching voucher codes');
    }
  },

  // Add a voucher code
  async addVoucherCode(voucherData: VoucherCodeSubmission): Promise<VoucherCode> {
    const { code, description, merchant, merchant_url, discount_value, discount_type, expiry_date, brand_image_url } = voucherData;
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) throw new Error('Not authenticated');
    
    try {
      // Use the secure RPC function
      const { data, error } = await supabase.rpc('secure_add_voucher_code', {
        code_param: code,
        description_param: description,
        merchant_param: merchant,
        merchant_url_param: merchant_url,
        discount_value_param: discount_value, // Pass discount_value as is (string)
        discount_type_param: discount_type,
        expiry_date_param: expiry_date,
        brand_image_url_param: brand_image_url
      });
      
      if (error) throw error;
      
      // Log the response for debugging
      console.log('Voucher code added successfully:', data);
      
      return data;
    } catch (error) {
      console.error('Error in addVoucherCode:', error);
      throw handleApiError(error, 'Error adding voucher code');
    }
  },
};

// Create a security wrapper for admin operations
const verifyAdminOperation = async <T>(
  operation: () => Promise<T>,
  errorMessage: string = 'Unauthorized admin operation'
): Promise<T> => {
  // First verify admin status
  const isAdminUser = await api.isAdmin();
  if (!isAdminUser) {
    logger.security('Unauthorized admin operation attempt', { errorMessage });
    throw new Error('Unauthorized: You do not have admin privileges');
  }
  
  try {
    // Execute the operation only if admin status is verified
    return await operation();
  } catch (error) {
    logger.error(errorMessage, error);
    throw handleApiError(error, errorMessage);
  }
};